/* File: src/contexts/EventContext.js */
import React, { createContext, useState, useEffect, useContext } from 'react';
import { publicApi } from '../services/api';

// Création du contexte
const EventContext = createContext();

export const useEvent = () => {
  const context = useContext(EventContext);
  if (!context) {
    console.warn('useEvent must be used within an EventProvider');
    // Retourner des valeurs par défaut pour éviter le crash
    return {
      eventId: null,
      eventDetails: null,
      loading: false,
      error: null,
      selectedCity: '',
      setSelectedCity: () => {},
      changeEvent: () => {},
      refreshEventDetails: () => {},
      userAgeVerified: false
    };
  }
  return context;
};

export const EventProvider = ({ children }) => {
  // État pour stocker l'ID de l'événement actif
  const [eventId, setEventId] = useState(null);
  const [eventDetails, setEventDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // État pour la ville sélectionnée
  const [selectedCity, setSelectedCity] = useState(() => {
    return localStorage.getItem('selectedCity') || '';
  });
  
  // Vérifier si l'utilisateur a validé son âge
  const userAgeVerified = !!localStorage.getItem('birthYear');
  
  // Fonction pour changer la ville sélectionnée
  const changeCity = (newCity) => {
    if (newCity) {
      localStorage.setItem('selectedCity', newCity);
      setSelectedCity(newCity);
      // Réinitialiser l'événement pour forcer le rechargement
      setEventDetails(null);
      setEventId(null);
      setError(null);
    }
  };
  
  // Fonction pour créer un événement par défaut basé sur la ville
  const createDefaultEvent = (city) => {
    const isDouala = city === 'Douala';
    return {
      id: isDouala ? '2' : '1',
      event_id: isDouala ? '2' : '1',
      name: `Guinness Bright Bar ${city}`,
      event_name: `Guinness Bright Bar ${city}`,
      date: isDouala ? '2025-07-12 19:00:00' : '2025-07-05 18:00:00',
      event_date: isDouala ? '2025-07-12 19:00:00' : '2025-07-05 18:00:00',
      location: isDouala ? 'Pullman Douala Rabingha, Bonanjo' : 'Hôtel Zingana, Bafoussam',
      city: city,
      description: `Vivez l'expérience unique du Guinness Bright Bar à ${city}.`,
      visuel: isDouala ? 'flyer-brightbar-douala.jpg' : 'flyer-brightbar-bafoussam.jpg'
    };
  };
  
  // Fonction pour obtenir l'événement à venir ou le plus récent
  const fetchLatestEvent = async () => {
    if (!userAgeVerified) {
      console.log('User age not verified, skipping event fetch');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Si une ville est sélectionnée, récupérer l'événement de cette ville
      if (selectedCity) {
        console.log('Fetching event for city:', selectedCity);
        
        try {
          const response = await publicApi.getEventByCity(selectedCity);
          
          if (response && response.data && response.data.success && response.data.event) {
            const cityEvent = response.data.event;
            const cityEventId = cityEvent.id || cityEvent.event_id;
            
            // Sauvegarder l'ID de l'événement dans localStorage
            localStorage.setItem('currentEventId', cityEventId.toString());
            
            // Mettre à jour l'état
            setEventId(cityEventId.toString());
            setEventDetails(cityEvent);
            setError(null);
            console.log('Event loaded successfully from API');
          } else {
            throw new Error('Format de réponse invalide');
          }
        } catch (apiError) {
          console.error('API Error, using fallback event:', apiError);
          
          // Utiliser un événement par défaut en cas d'erreur
          const fallbackEvent = createDefaultEvent(selectedCity);
          setEventId(fallbackEvent.id);
          setEventDetails(fallbackEvent);
          localStorage.setItem('currentEventId', fallbackEvent.id);
          setError('Utilisation des données en cache');
        }
      } else {
        console.log('No city selected, using default event');
        
        try {
          // Pas de ville sélectionnée, récupérer l'événement le plus récent
          const response = await publicApi.getLatestEvent();
          
          if (response && response.data && response.data.success && response.data.event) {
            const latestEvent = response.data.event;
            const latestEventId = latestEvent.id || latestEvent.event_id;
            
            // Sauvegarder l'ID de l'événement dans localStorage
            localStorage.setItem('currentEventId', latestEventId.toString());
            
            // Mettre à jour l'état
            setEventId(latestEventId.toString());
            setEventDetails(latestEvent);
            setError(null);
          } else {
            throw new Error('Format de réponse invalide');
          }
        } catch (apiError) {
          console.error('API Error, using default event:', apiError);
          
          // Utiliser un événement par défaut
          const defaultEvent = createDefaultEvent('Douala');
          setEventId(defaultEvent.id);
          setEventDetails(defaultEvent);
          setError('Utilisation des données en cache');
        }
      }
    } catch (err) {
      console.error('Error fetching event:', err);
      setError('Impossible de récupérer les informations de l\'événement');
      
      // Même en cas d'erreur, définir un événement par défaut
      const errorFallbackEvent = createDefaultEvent(selectedCity || 'Douala');
      setEventId(errorFallbackEvent.id);
      setEventDetails(errorFallbackEvent);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour charger les détails d'un événement spécifique
  const fetchEventDetails = async (id) => {
    if (!id || !userAgeVerified) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await publicApi.getEventById(id);
      
      if (response && response.data && response.data.success) {
        // Extraire les détails de l'événement
        if (response.data.event) {
          setEventDetails(response.data.event);
          setError(null);
        } else {
          setEventDetails(response.data);
          setError(null);
        }
      } else {
        throw new Error('Format de réponse invalide');
      }
    } catch (err) {
      console.error('Error loading event details:', err);
      setError('Une erreur est survenue lors du chargement des détails de l\'événement');
      
      // Utiliser un événement par défaut basé sur la ville sélectionnée
      const fallbackEvent = createDefaultEvent(selectedCity || 'Douala');
      setEventDetails(fallbackEvent);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour changer l'événement actif (si nécessaire pour d'autres fonctionnalités)
  const changeEvent = (newEventId) => {
    if (newEventId) {
      localStorage.setItem('currentEventId', newEventId);
      setEventId(newEventId);
    }
  };
  
  // Effet initial pour charger l'événement
  useEffect(() => {
    if (userAgeVerified) {
      // Utiliser un timeout pour éviter les appels multiples rapides
      const timer = setTimeout(() => {
        if (selectedCity || !eventDetails) {
          fetchLatestEvent();
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [userAgeVerified, selectedCity]);
  
  // Effet secondaire pour charger les détails quand l'ID change
  useEffect(() => {
    if (eventId && userAgeVerified && !eventDetails) {
      const timer = setTimeout(() => {
        fetchEventDetails(eventId);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [eventId, userAgeVerified]);
  
  // Surveiller les changements dans localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'currentEventId' && e.newValue !== eventId) {
        setEventId(e.newValue);
      }
      
      if (e.key === 'selectedCity' && e.newValue !== selectedCity) {
        setSelectedCity(e.newValue || '');
      }
      
      // Vérifier si birthYear a été ajouté
      if (e.key === 'birthYear' && e.newValue && !userAgeVerified) {
        fetchLatestEvent();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [eventId, selectedCity, userAgeVerified]);
  
  const value = {
    eventId,
    eventDetails,
    loading,
    error,
    selectedCity,
    setSelectedCity: changeCity,
    changeEvent,
    refreshEventDetails: () => {
      if (selectedCity || eventId) {
        fetchLatestEvent();
      }
    },
    userAgeVerified
  };
  
  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
};

export default EventContext;